import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Actions, Categories } from '../../../../types/gtm';
import environmentConfig from '../../../../config/environment-configs';
import Content from '../../../../components/Section/Content';
import Box from '../../../../components/Section/Box';
import List from '../../../../components/Section/List';
import Icon from '../../../../components/Section/Icon';
import svg from '../../../../static/img/icon-open-menu.svg';
import {
  ProfileDefaultPic,
  ProfilePic,
  UnderageProfile,
  Description,
  ResponsibleUnderagePending,
} from './styles';
import Modal from '../../../../components/Modal';
import { ModalBody, ModalFooter } from '../../../../components/Modal/styles';
import { DisplayEmail } from '../BoxUnderageInfo/styles';
import Button from '../../../../components/Button';
import { ReactComponent as Clock } from '../../../../static/img/clock.svg';
import { UnderageUser } from '../../../../types/underageUser';
import Action from '../../../../components/Section/Action';
import { useAuth } from '../../../../hooks/useAuth';

type BoxParentConsentProps = {
  underageUsers: UnderageUser[];
};

type ModalState = {
  show: boolean;
  email?: string;
};

export default function BoxParentConsent({
  underageUsers,
}: BoxParentConsentProps) {
  const history = useHistory();
  const boxParentalRef = useRef<HTMLElement>(null);
  const location = useLocation();
  window.dataLayer = window.dataLayer || [];

  const [modal, setModal] = useState<ModalState>({
    show: false,
  });

  const title = 'Consentimento Parental';
  const describeTitle =
    'Autorize, bloqueie ou exclua a conta dos menores vinculadas a sua conta Globo';
  const { user } = useAuth();

  interface ProfilePictureProps {
    picture: string;
    name: string;
  }

  const ProfileSummaryPicture = ({ picture, name }: ProfilePictureProps) => {
    const isEmpty = (str: string) => !str || str.length === 0;

    if (!isEmpty(picture)) {
      return <ProfilePic aria-hidden src={picture} alt="profile-pic" />;
    }

    const getFirstLetter = (text: string) =>
      text ? text.charAt(0).toUpperCase() : '';
    return (
      <ProfileDefaultPic aria-hidden>{getFirstLetter(name)}</ProfileDefaultPic>
    );
  };

  function verifyManage(e: any, parent: UnderageUser) {
    if (e.currentTarget) {
      if (!parent.canBeManaged) {
        setModal({ show: true, email: parent.email });
        return;
      }

      if (!parent.authorizationAt && parent.hash) {
        window.open(
          `${environmentConfig.PARENTAL_CONSENT_SERVICE_LOGIN_PAGE}/responsavel/autorizacao/${parent.hash}`,
          '_blank',
        );

        return;
      }

      window.dataLayer.push({
        event: 'customEventUserInteraction',
        event_category: Categories.Home,
        event_action: `${Actions.ClickParentalConsent}`,
        event_label: `${parent.underageId}`,
        bd_suser_code: user?.globoId,
      });

      history.push({
        pathname: `/consentimento-parental/${parent.underageId}`,
      });
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  useEffect(() => {
    const { hash } = location;

    if (boxParentalRef.current && hash === '#controle-parental') {
      boxParentalRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [location.hash]);

  return (
    <>
      <Box ref={boxParentalRef} title={title} describeTitle={describeTitle}>
        <Content>
          <List>
            {underageUsers.map(parent => (
              <React.Fragment key={parent.underageId}>
                <li>
                  <Action
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      verifyManage(e, parent);
                    }}
                    theme="button"
                    type="button"
                  >
                    <UnderageProfile>
                      <ProfileSummaryPicture picture="" name={parent.name} />
                      <div>
                        <p>{parent.name}</p>
                        {parent.authorizationAt ? (
                          <DisplayEmail isControlParental>
                            {parent.email}
                          </DisplayEmail>
                        ) : (
                          <>
                            <ResponsibleUnderagePending>
                              <Clock fill="#666666" />
                              <Description>{`Autorize até ${formatDate(
                                parent.approvalDeadline || '',
                              )}`}</Description>
                            </ResponsibleUnderagePending>
                          </>
                        )}
                      </div>
                    </UnderageProfile>

                    <Icon svg={svg} alt="icon-open-menu" />
                  </Action>
                </li>
                <hr aria-hidden />
              </React.Fragment>
            ))}
          </List>
        </Content>
      </Box>
      {modal.show && (
        <Modal
          header="Não é possível realizar a ação"
          closeButton={() => setModal({ show: false })}
        >
          <ModalBody>
            <p>
              Não é possível realizar a ação, pois existem uma ou mais
              assinaturas associadas à conta de <b>{modal.email}</b>.
            </p>
            <p>
              Para excluir ou bloquear a Conta Globo do menor, solicite que ele
              cancele as assinaturas no Minha Conta.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="primary" onClick={() => setModal({ show: false })}>
              Fechar
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
}
